import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import PersonaBadge from '../common/PersonaBadge';
import { PersonaMarkdown } from '../personas/PersonaMarkdown';
import { gymnasiumService } from '../../services/gymnasiumService';
import { GauntletRun } from '../../types/gymnasium';
import GauntletScorecard from './GauntletScorecard';
import { FiArrowLeft, FiCheck, FiX, FiAlertTriangle, FiClock } from 'react-icons/fi';

interface Props {
  runId: string;
}

const GauntletRunView: React.FC<Props> = ({ runId }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [run, setRun] = useState<GauntletRun | null>(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<any>(null);

  // Manual judgment state
  const [judgScore, setJudgScore] = useState(5);
  const [judgCriteria, setJudgCriteria] = useState<Record<number, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = async () => {
    try {
      const data = await gymnasiumService.getRunStatus(runId);
      setRun(data);
      return data.status;
    } catch (err) {
      console.error('Failed to fetch run status:', err);
      return 'error';
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(async () => {
      const status = await fetchStatus();
      if (status !== 'running') {
        clearInterval(pollRef.current);
      }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [runId]);

  const handleSubmitJudgment = async () => {
    if (!run?.awaiting_judgment) return;
    setSubmitting(true);
    try {
      // Find the pending scenario's criteria from the results
      const pendingResult = run.results?.find((r: any) => r.scenario_id === run.awaiting_judgment);
      const criteria = pendingResult?.criteria || [];
      const criteriaResults = criteria.map((c: string, i: number) => ({
        criterion: c,
        passed: judgCriteria[i] ?? false,
        reason: 'Manual judgment',
      }));
      await gymnasiumService.submitJudgment(runId, {
        scenario_id: run.awaiting_judgment,
        score: judgScore,
        criteria_results: criteriaResults,
      });
      setJudgScore(5);
      setJudgCriteria({});
      fetchStatus();
    } catch (err) {
      console.error('Failed to submit judgment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-5 ${
    darkMode ? 'bg-gray-800 border-amber-600' : 'bg-white border-amber-500 shadow-sm'
  }`;

  if (loading) {
    return <div className="space-y-4 animate-pulse"><div className={`h-48 rounded-xl ${darkMode ? 'bg-gray-800/60' : 'bg-gray-100'}`} /></div>;
  }

  if (!run) {
    return <div className={`text-center py-12 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Run not found</div>;
  }

  // Show scorecard if completed
  if (run.status === 'completed') {
    return <GauntletScorecard run={run} />;
  }

  const progress = run.scenarios_total > 0 ? (run.scenarios_completed / run.scenarios_total) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/gymnasium/gauntlets')} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
          <FiArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-400">
            {run.gauntlet_name?.replace(/_/g, ' ')}
          </h1>
          <div className="flex items-center gap-2 mt-0.5">
            <PersonaBadge personaId={run.persona_id} personaName={run.persona_name} size="xs" clickable={false} />
            <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {run.judge_mode} mode · {run.scenarios_completed}/{run.scenarios_total} scenarios
            </span>
          </div>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full font-medium ${
          run.status === 'running' ? 'bg-amber-500/20 text-amber-400 animate-pulse' :
          run.status === 'failed' ? 'bg-red-500/20 text-red-400' :
          'bg-green-500/20 text-green-400'
        }`}>
          {run.status === 'running' && run.awaiting_judgment ? 'Awaiting Judgment' : run.status}
        </span>
      </div>

      {/* Progress Bar */}
      <div className={sectionClass}>
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Progress</span>
          <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{Math.round(progress)}%</span>
        </div>
        <div className={`h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <div
            className="h-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Failed banner */}
      {run.status === 'failed' && (
        <div className="px-4 py-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 text-sm flex items-center gap-2">
          <FiAlertTriangle className="w-4 h-4 shrink-0" />
          Run failed — showing partial results ({run.scenarios_completed} of {run.scenarios_total} completed)
        </div>
      )}

      {/* Scenario Results */}
      <div className="space-y-3">
        {run.results?.map((r: any, i: number) => (
          <div key={r.scenario_id || i} className={sectionClass}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                {r.label || `Scenario ${i + 1}`}
              </span>
              <div className="flex items-center gap-2">
                {r.score != null && (
                  <span className={`text-sm font-bold ${r.score >= r.max_score * 0.7 ? 'text-green-400' : r.score >= r.max_score * 0.4 ? 'text-amber-400' : 'text-red-400'}`}>
                    {r.score}/{r.max_score}
                  </span>
                )}
                <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{r.category}</span>
              </div>
            </div>
            {r.response && (
              <div className="mb-2 max-h-[120px] overflow-y-auto">
                <PersonaMarkdown content={r.response} size="small" />
              </div>
            )}
            {r.criteria_results && (
              <div className="flex gap-1.5 flex-wrap">
                {r.criteria_results.map((cr: any, ci: number) => (
                  <span key={ci} className={`text-[10px] px-2 py-0.5 rounded flex items-center gap-1 ${
                    cr.passed ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'
                  }`}>
                    {cr.passed ? <FiCheck className="w-2.5 h-2.5" /> : <FiX className="w-2.5 h-2.5" />}
                    {cr.criterion?.slice(0, 40)}{cr.criterion?.length > 40 ? '...' : ''}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Errors */}
        {run.errors?.map((e: any, i: number) => (
          <div key={`err-${i}`} className={`${sectionClass} border-red-500/20`}>
            <div className="flex items-center gap-2">
              <FiAlertTriangle className="w-4 h-4 text-red-400" />
              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{e.label || `Scenario`}</span>
              <span className="text-xs text-red-400">{e.error}</span>
            </div>
          </div>
        ))}

        {/* Pending scenario (running state) */}
        {run.status === 'running' && !run.awaiting_judgment && (
          <div className={`${sectionClass} animate-pulse`}>
            <div className="flex items-center gap-2">
              <FiClock className="w-4 h-4 text-amber-400" />
              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Running scenario {run.scenarios_completed + 1} of {run.scenarios_total}...
              </span>
            </div>
          </div>
        )}

        {/* Manual judgment UI */}
        {run.awaiting_judgment && (
          <div className={`${sectionClass} border-amber-500/30`}>
            <h3 className="text-sm font-semibold text-amber-400 mb-3">Your Judgment Required</h3>
            <div className="mb-3">
              <label className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Score (0-10)</label>
              <input
                type="range" min={0} max={10} value={judgScore}
                onChange={e => setJudgScore(Number(e.target.value))}
                className="w-full mt-1"
              />
              <span className="text-lg font-bold text-amber-400">{judgScore}</span>
            </div>
            <button
              onClick={handleSubmitJudgment}
              disabled={submitting}
              className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Judgment'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GauntletRunView;
